/**
 * useDestinationVeil — single source of truth for the hidden-destination
 * masking system (VÉRAULT Member Suite · Wave 26.20 Phase 1b).
 *
 * The advisor toggles `reveal_destination = "masked"` on a trip; from
 * that moment on, NO client-facing surface may render the real
 * destination/city/country/airport/landmark image or text. Every UI
 * component that wants to describe "where" must route through this
 * hook so masking happens in exactly one place.
 *
 * Returns:
 *   {
 *     veiled,        // boolean — true when the trip is masked
 *     label,         // string — what to show as the destination title
 *     dateLabel,     // string — what to show as the date range
 *     image,         // string — image URL to render (atmospheric if veiled)
 *     atmosphere,    // boolean — true when the image is an atmosphere stand-in
 *     eyebrow,       // string — small mono caption ("Awaiting reveal" vs "Experience")
 *   }
 *
 * Phase 1b builds the hook + a default curated atmosphere pool. Phase 2
 * will route AI-generated copy + the live weather chip through the same
 * veil; consumers will gain those automatically.
 */
import { formatDateRange } from "../portal/lib/format";

// Curated atmospheric stand-in images — never landmark, never identifiable.
// Pexels 4K, free to use. Pool is rotated deterministically by trip.id so
// each masked trip keeps a STABLE atmosphere across reloads (it would feel
// uncanny if "your secret destination" changed image on every refresh).
// Expanded in Wave 36 Phase 1 (Feb 2026) across season + mood categories.
const ATMOSPHERE_POOL = [
  // candlelight / warm interior
  "https://images.pexels.com/photos/1123262/pexels-photo-1123262.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // abstract softness / texture
  "https://images.pexels.com/photos/3680219/pexels-photo-3680219.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // wave / horizon
  "https://images.pexels.com/photos/1295138/pexels-photo-1295138.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // calm water
  "https://images.pexels.com/photos/2387418/pexels-photo-2387418.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // misty landscape
  "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // dusk silhouette
  "https://images.pexels.com/photos/1366919/pexels-photo-1366919.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // diffused atmosphere
  "https://images.pexels.com/photos/1144176/pexels-photo-1144176.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // winter — snow drift / pine forest at dusk
  "https://images.pexels.com/photos/688660/pexels-photo-688660.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // winter — frosted branches / white sky
  "https://images.pexels.com/photos/688835/pexels-photo-688835.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // desert — golden dunes
  "https://images.pexels.com/photos/847402/pexels-photo-847402.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // desert — soft sand ripples
  "https://images.pexels.com/photos/1731660/pexels-photo-1731660.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // coast — quiet beach pre-dawn
  "https://images.pexels.com/photos/189349/pexels-photo-189349.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // coast — moody ocean texture
  "https://images.pexels.com/photos/355288/pexels-photo-355288.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // city — anonymous evening lights (no skyline silhouette)
  "https://images.pexels.com/photos/315191/pexels-photo-315191.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // city — bokeh / soft amber light
  "https://images.pexels.com/photos/167699/pexels-photo-167699.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // mountain — soft fog through valley
  "https://images.pexels.com/photos/167698/pexels-photo-167698.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // forest — diffused morning light through trees
  "https://images.pexels.com/photos/268415/pexels-photo-268415.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // spring — calm wildflower meadow (no landmark)
  "https://images.pexels.com/photos/56875/tulips-meadow-tulip-fields-tulip-fields-56875.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // summer — warm grain texture / golden hour field
  "https://images.pexels.com/photos/325944/pexels-photo-325944.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // autumn — soft amber leaves close-up
  "https://images.pexels.com/photos/235621/pexels-photo-235621.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // autumn — quiet path through trees
  "https://images.pexels.com/photos/235615/pexels-photo-235615.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // night — softly lit lantern / candle close
  "https://images.pexels.com/photos/207962/pexels-photo-207962.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // tropical — palm shadow against soft sky (no skyline)
  "https://images.pexels.com/photos/1430676/pexels-photo-1430676.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // architecture-mood — abstract arch with warm shadow (no facade clue)
  "https://images.pexels.com/photos/2724664/pexels-photo-2724664.jpeg?auto=compress&cs=tinysrgb&w=1920",
];

function pickAtmosphere(seed) {
  if (!seed) return ATMOSPHERE_POOL[0];
  // Deterministic hash from the trip id so a masked trip keeps the SAME
  // atmosphere on every render.
  let n = 0;
  for (let i = 0; i < seed.length; i += 1) n = (n * 31 + seed.charCodeAt(i)) >>> 0;
  return ATMOSPHERE_POOL[n % ATMOSPHERE_POOL.length];
}

export function useDestinationVeil(trip) {
  if (!trip) {
    return {
      veiled: false,
      label: "",
      dateLabel: "",
      image: null,
      atmosphere: false,
      eyebrow: "Experience",
    };
  }

  const destVeiled = (trip.reveal_destination || "full") !== "full";
  const datesVeiled = (trip.reveal_dates || "full") === "hidden";
  const datesPartial = (trip.reveal_dates || "full") === "partial";

  // Real values
  const realLabel = trip.client_trip_title || trip.destinations || "Untitled experience";
  const realImage = trip.hero_image_url || null;
  const realDateRange = formatDateRange(
    trip.trip_start_date || trip.start_date,
    trip.trip_end_date || trip.end_date,
  );

  // Veiled values — Phase 1a/1b user choice: default mask is "Secret Destination".
  // We keep custom advisor overrides if set (some legacy trips have the
  // playful "A surprise awaits ✨" mask — respect that).
  const mask = trip.reveal_destination_mask && trip.reveal_destination_mask.trim();
  const veiledLabel = mask || "Secret Destination";
  // If the advisor pinned a specific atmosphere image use it; otherwise
  // pick a stable one from the curated pool.
  const veiledImage =
    trip.reveal_destination_image_url
    || (destVeiled ? pickAtmosphere(trip.id) : null);

  return {
    veiled: destVeiled,
    label: destVeiled ? veiledLabel : realLabel,
    dateLabel: datesVeiled
      ? (trip.reveal_dates_mask || "Dates revealed soon")
      : datesPartial
        ? (trip.reveal_dates_mask || realDateRange || "")
        : realDateRange,
    image: destVeiled ? veiledImage : realImage,
    atmosphere: destVeiled,
    eyebrow: destVeiled ? "Awaiting reveal" : "Experience",
  };
}

export default useDestinationVeil;
